import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useUpdateMerchValue, RiskMerchValue } from "@/lib/api/risk/merchValues";
import { MerchValuesForm } from "./MerchValuesForm";
import { MerchValueFormValues } from "./form-schema";
import { apiToForm, formToUpdateApi } from "./transform";

interface EditMerchValueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchValue: RiskMerchValue;
  /** All existing merch value items (used for overlap detection) */
  existingItems: RiskMerchValue[];
}

export function EditMerchValueDialog({
  open,
  onOpenChange,
  merchValue,
  existingItems,
}: EditMerchValueDialogProps) {
  const { toast } = useToast();
  const updateMerchValue = useUpdateMerchValue();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues: Partial<MerchValueFormValues> = apiToForm(merchValue);

  const handleSubmit = async (values: MerchValueFormValues) => {
    setIsSubmitting(true);
    try {
      const payload = formToUpdateApi(values);
      const result = await updateMerchValue(merchValue.id, payload);

      if (result.success) {
        toast({ title: "Success", description: "Merch value updated successfully" });
        onOpenChange(false);
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to update merch value",
          variant: "destructive",
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "An error occurred";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Merch Value</DialogTitle>
        </DialogHeader>
        <MerchValuesForm
          initialValues={initialValues}
          disableIdentifiers
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          submitLabel="Update"
          existingItems={existingItems}
          excludeId={merchValue.id}
        />
      </DialogContent>
    </Dialog>
  );
}
